import { useEffect, useState } from "react";
import { toast } from "sonner";
import apiClient, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { FloppyDisk, LockSimple } from "@phosphor-icons/react";

const NUMERIC = [
  { key: "target_gross_margin", label: "Target Gross Margin (%)" },
  { key: "minimum_gross_margin", label: "Minimum Gross Margin (%)" },
  { key: "equipment_markup", label: "Equipment Markup (%)" },
  { key: "labor_billing_rate", label: "Installation Billable ($/hr)" },
  { key: "programming_rate", label: "Programming Billable ($/hr)" },
  { key: "engineering_rate", label: "Engineering Billable ($/hr)" },
  { key: "project_management_rate", label: "Project Mgmt Billable ($/hr)" },
  { key: "service_rate", label: "Service Billable ($/hr)" },
  { key: "lead_tech_cost", label: "Lead Tech Cost ($/hr)" },
  { key: "junior_tech_cost", label: "Junior Tech Cost ($/hr)" },
  { key: "project_manager_cost", label: "PM Cost ($/hr)" },
  { key: "engineering_cost", label: "Engineering Cost ($/hr)" },
];

const TEXTAREAS = [
  { key: "preferred_manufacturers", label: "Preferred Manufacturers", rows: 2 },
  { key: "standard_warranty_language", label: "Standard Warranty Language" },
  { key: "required_exclusions", label: "Required Exclusions" },
  { key: "change_order_policy", label: "Change Order Policy" },
  { key: "payment_terms", label: "Payment Terms" },
  { key: "client_expectation_language", label: "Client Expectation Language" },
  { key: "red_flag_rules", label: "Red Flag Rules", rows: 9 },
];

const inputCls = "w-full h-11 px-3.5 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { apiClient.get("/company/standards").then((r) => setForm(r.data)); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.company_id;
      NUMERIC.forEach(({ key }) => (payload[key] = parseFloat(payload[key]) || 0));
      await apiClient.put("/company/standards", payload);
      toast.success("Company standards updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400 animate-pulse">Loading standards…</div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">Company Standards</h1>
          <p className="text-slate-500 mt-2">Your rates, markup and policies. Proposal Forensics compares proposals against these as your <span className="font-semibold">Company Standard</span> — distinct from industry benchmarks and Proposal Forensics recommendations.</p>
        </div>
        {isAdmin && <button data-testid="save-standards-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors disabled:opacity-60"><FloppyDisk size={17} weight="bold" /> {saving ? "Saving…" : "Save Standards"}</button>}
      </div>

      {!isAdmin && <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3" data-testid="readonly-notice"><LockSimple size={16} weight="bold" /> Read-only. Only an admin can edit company standards.</div>}

      <fieldset disabled={!isAdmin} className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-display font-bold text-lg text-[#0F172A] mb-5">Financial Targets, Rates &amp; Costs</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {NUMERIC.map(({ key, label }) => (
              <div key={key}>
                <label className="font-mono-plex text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</label>
                <input data-testid={`std-${key}`} type="number" className={`${inputCls} mt-2`} value={form[key] ?? ""} onChange={set(key)} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
          <h2 className="font-display font-bold text-lg text-[#0F172A]">Policies &amp; Language</h2>
          {TEXTAREAS.map(({ key, label, rows }) => (
            <div key={key}>
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</label>
              <textarea data-testid={`std-${key}`} rows={rows || 3} className="mt-2 w-full px-3.5 py-3 rounded-md border border-slate-300 bg-white text-sm leading-relaxed focus:ring-2 focus:ring-slate-900 focus:border-slate-900 focus:outline-none transition" value={form[key] ?? ""} onChange={set(key)} />
            </div>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
